import { DatastoreObjectType, getObjectHash, ObjectHashhMapType, objectsMatch } from '@iotv/iotv-v3-types'

/** map of which items have been created (new), deleted, changed or remain the same, in an ObjectHashMap */
export type ChangeContext<T extends DatastoreObjectType = DatastoreObjectType> = {
  new: T[]
  /** Object Hash will only know keys for deleted items */
  deleted: DatastoreObjectType[]
  changed: T[]
  same: T[]
}

/** A utility class to hold hashes for change comparison */
export class ObjectHashMap {
  map: ObjectHashhMapType = {}

  /** get a stored hash, returns undefined if not previously calculated */
  getHash = <T extends DatastoreObjectType>(item: T) => {
    const { pk, sk } = item
    return this.map[pk]?.[sk] as string | undefined
  }

  add = <T extends DatastoreObjectType>(item: T) => {
    const { pk, sk } = item
    const hash = getObjectHash(item)
    if (!this.map[pk]) this.map[pk] = {}
    this.map[pk][sk] = hash
  }

  remove = <T extends DatastoreObjectType>(item: T) => {
    const { pk, sk } = item
    if (!this.isKnown) return false
    delete this.map[pk][sk]
  }

  isKnown = <T extends DatastoreObjectType>(item: T) => {
    const { pk, sk } = item
    return !!this.map[pk]?.[sk]
  }

  isNew = <T extends DatastoreObjectType>(item: T) => !this.isKnown(item)

  isSame = <T extends DatastoreObjectType>(item: T) => {
    const currentHash = getObjectHash(item)
    return this.isKnown(item) && currentHash === this.getHash(item)
  }

  hasChanged = <T extends DatastoreObjectType>(item: T) => {
    return this.isKnown(item) && !this.isSame(item)
  }

  containedDatastoreObjectKeys = () =>
    Object.entries(this.map).reduce((a, [pk, skMap]) => {
      a.push(
        ...Object.keys(skMap).map((sk) => {
          return { pk, sk }
        }),
      )
      return a
    }, [] as DatastoreObjectType[])

  /** return list of keys that have been hashed but are not present in provided items list  */
  getHashedItemsNotPresent = <T extends DatastoreObjectType>(items: T[]) =>
    this.containedDatastoreObjectKeys().filter((dok) => !items.find(({ sk, pk }) => objectsMatch(dok, { sk, pk })))

  getChangeState = <T extends DatastoreObjectType>(items: T[]): ChangeContext<T> => {
    const deleted = this.getHashedItemsNotPresent(items)
    const changeContext: ChangeContext<T> = { new: [], changed: [], deleted, same: [] }

    return items.reduce((a, c) => {
      const isNew = this.isNew(c)
      const isChanged = this.hasChanged(c)
      const isSame = this.isSame(c)
      let checksum = [isNew, isChanged, isSame].reduce((a, c) => {
        if (c) {
          a++
        }
        return a
      }, 0)
      if (deleted.find((item) => item.sk === c.sk && item.pk === c.pk)) {
        checksum++
      }

      if (checksum > 1) {
        throw new Error(`Err: getChangeState, more than one change state for an item ${c.sk}`)
      }
      if (checksum == 0) {
        throw new Error(`Err: getChangeState, has not categorised item ${c.sk}`)
      }
      if (isNew) {
        a.new.push(c)
      }
      if (isChanged) {
        a.changed.push(c)
      }
      if (isSame) {
        a.same.push(c)
      }
      return a
    }, changeContext)
  }
}
